import React, { useRef, useState, useEffect } from 'react';
import { CreditCard, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DESIGN_WIDTH = 500;

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${API_URL}${url}`;
};

const getLogoShapeStyle = (shape) => {
  switch (shape) {
    case 'square': return { borderRadius: '0%' };
    case 'rounded-rect': return { borderRadius: '16%' };
    case 'circle': default: return { borderRadius: '50%' };
  }
};

const QRPlaceholder = ({ position, scale }) => {
  const color = position.color || '#ffffff';
  return (
    <div className="absolute flex flex-col items-center justify-center"
      style={{
        left: `${position.x}%`, top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        width: `${position.size}%`, pointerEvents: 'none'
      }}>
      <div className="w-full aspect-square rounded"
        style={{ border: `1.5px dashed ${color}60`, backgroundColor: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg viewBox="0 0 24 24" className="w-1/2 h-1/2" fill="none" stroke={color} strokeWidth="1.5" style={{ opacity: 0.45 }}>
          <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="3" height="3" /><rect x="18" y="14" width="3" height="3" /><rect x="14" y="18" width="3" height="3" /><rect x="18" y="18" width="3" height="3" />
        </svg>
      </div>
      <span style={{ fontSize: `${Math.max(5, 9 * scale)}px`, color: `${color}80` }}>pipercard.com</span>
    </div>
  );
};

const LogoPlaceholder = ({ position }) => {
  if (!position) return null;
  const shape = position.shape || 'circle';
  
  // If logo has an image, show it
  if (position.image) {
    return (
      <div className="absolute flex items-center justify-center overflow-hidden pointer-events-none"
        style={{
          left: `${position.x}%`, top: `${position.y}%`,
          width: `${position.size}%`,
          ...(shape === 'rounded-rect' ? { aspectRatio: '1.4/1' } : { aspectRatio: '1' }),
          transform: 'translate(-50%, -50%)',
          ...getLogoShapeStyle(shape),
        }}>
        <img src={resolveUrl(position.image)} alt="" className="w-full h-full object-cover" draggable={false} style={getLogoShapeStyle(shape)} />
      </div>
    );
  }
  
  // No image - show generic placeholder (not Piper Card logo)
  return (
    <div className="absolute flex flex-col items-center justify-center overflow-hidden pointer-events-none"
      style={{
        left: `${position.x}%`, top: `${position.y}%`,
        width: `${position.size}%`,
        ...(shape === 'rounded-rect' ? { aspectRatio: '1.4/1' } : { aspectRatio: '1' }),
        transform: 'translate(-50%, -50%)',
        border: '1.5px dashed rgba(212,175,55,0.3)',
        backgroundColor: 'rgba(212,175,55,0.05)',
        ...getLogoShapeStyle(shape),
      }}>
      <Upload className="w-1/3 h-1/3 text-amber-500/40" />
      <span className="text-[8px] font-medium text-amber-500/40 uppercase mt-0.5">Logo</span>
    </div>
  );
};

const CardSide = ({ bgColor, bgImage, textElements, label, qrPosition = null, logoPosition = null }) => {
  const cardRef = useRef(null);
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setScale(entry.contentRect.width / DESIGN_WIDTH);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
      <div
        ref={cardRef}
        className="relative w-full rounded-lg shadow-md overflow-hidden border border-white/10"
        style={{
          aspectRatio: '3.375 / 2.125',
          backgroundColor: bgColor || '#1e293b',
          backgroundImage: bgImage ? `url(${resolveUrl(bgImage)})` : 'none',
          backgroundSize: 'cover', backgroundPosition: 'center',
        }}
        data-testid={`template-preview-${label}`}
      >
        {textElements.map((el) => (
          <div key={el.id} className="absolute whitespace-nowrap"
            style={{
              left: `${el.x}%`, top: `${el.y}%`,
              transform: 'translate(-50%, -50%)',
              fontSize: `${el.fontSize * scale}pt`,
              fontFamily: el.fontFamily || 'Inter',
              fontWeight: el.fontWeight || 'normal',
              color: el.color || '#FFFFFF',
              textAlign: el.textAlign || 'center',
              pointerEvents: 'none', lineHeight: 1.2,
            }}
          >
            {el.text}
          </div>
        ))}
        {qrPosition && <QRPlaceholder position={qrPosition} scale={scale} />}
        {logoPosition && <LogoPlaceholder position={logoPosition} />}
      </div>
      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</span>
    </div>
  );
};

const TemplateCardPreview = ({ template, className = '' }) => {
  const frontBgColor = template.front_bg_color || template.background_color || '#1e293b';
  const frontBgImage = template.front_bg_image;
  const frontText = template.front_text_elements?.length
    ? template.front_text_elements
    : (template.default_text_elements || []);

  const backBgColor = template.back_bg_color || template.background_color || '#1e293b';
  const backBgImage = template.back_bg_image;
  const backText = template.back_text_elements || [];

  const hasFront = frontBgImage || frontText.length > 0 || template.front_image;
  const hasBack = backBgImage || backText.length > 0 || template.back_image;

  if (!hasFront && !hasBack) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-gray-100 ${className}`}>
        <CreditCard className="w-12 h-12 text-gray-300" />
      </div>
    );
  }

  return (
    <div className={`flex gap-3 items-start justify-center p-4 bg-gray-50 ${className}`} data-testid="template-preview">
      <CardSide bgColor={frontBgColor} bgImage={frontBgImage} textElements={frontText} label="Front" logoPosition={template.front_logo_position || null} />
      <CardSide bgColor={backBgColor} bgImage={backBgImage} textElements={backText} label="Back" qrPosition={template.back_qr_position || null} logoPosition={template.back_logo_position || null} />
    </div>
  );
};

export default TemplateCardPreview;
